import React, { useEffect, useState } from "react";
import axios from "../../utils/axiosConfig";
import { useAuth } from "../../context/AuthContext";
import ClubEditDialog from "./ClubEditDialog";

import { Container, Paper, Typography, Box, Button, Tooltip, Avatar } from "@mui/material";
import { 
  DataGrid, 
  GridColDef,
  GridToolbar
} from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BusinessIcon from "@mui/icons-material/Business";
import ClubLogo from '../../components/ClubLogo';

import { handleApiError } from "../../utils/apiErrorHandler";

interface Club {
  _id: string;
  name: string;
  address: {
    street: string;
    postalCode: string;
    city: string;
  };
  hasLogo?: boolean;
  contactPerson: {
    _id: string;
    name: string;
    email: string;
  };
}

/*const ClubLogo: React.FC<{ clubId: string, hasLogo: boolean }> = ({ clubId, hasLogo }) => {
  if (!hasLogo) {
    return <BusinessIcon color="primary" fontSize="small" />;
  }
  
  return (
    <Avatar 
      src={`${baseURL}/api/images/Club/${clubId}/thumbnail`}
      alt="Club Logo"
      sx={{ width: 32, height: 32 }}
      variant="rounded"
    >
      <BusinessIcon fontSize="small" />
    </Avatar>
  );
};*/

const ClubsManagementPage: React.FC = () => {
  const [clubs, setClubs] = useState<Club[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const {userRole} = useAuth();
  
  // Dialog state
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [selectedClub, setSelectedClub] = useState<Club | null>(null);

  const fetchClubs = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/clubs");
      setClubs(response.data);
      setError(null);
    } catch (err) {
      handleApiError(error, "Fehler beim Laden der Vereine.");
      setError("Fehler beim Laden der Vereine.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClubs();
  }, []);

  const handleEdit = (club: Club) => {
    setSelectedClub(club);
    setEditDialogOpen(true);
  };

  const handleClubUpdated = (updatedClub: Club) => {
    // Update local state
    setClubs(prevClubs => 
      prevClubs.map(club => 
        club._id === updatedClub._id ? updatedClub : club
      )
    );
  };

  const handleDelete = (club: Club) => {
    // Disabled, but kept for future implementation
    alert(`Löschen von ${club.name}`);
  };

  const getFullAddress = (club: Club) => {
    return `${club.address.street}, ${club.address.postalCode} ${club.address.city}`;
  };

  const getContactInfo = (club: Club) => {
    // Check if contactPerson exists and has the expected structure
    if (club.contactPerson && club.contactPerson.name && club.contactPerson.email) {
      return `${club.contactPerson.name} (${club.contactPerson.email})`;
    }
    // Fallback if the structure is different
    return "Keine Kontaktinformationen verfügbar";
  };

  const columns: GridColDef[] = [
    { 
      field: "logo", 
      headerName: "", 
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const club = params.row as Club;
        return <ClubLogo clubId={club._id} hasLogo={club.hasLogo || false} size="small" />;
      }
    },
    { 
      field: "name", 
      headerName: "Vereinsname", 
      flex: 1, 
      minWidth: 200,
      renderCell: (params) => {
        const club = params.row as Club;
        return (
          <Typography>{club.name}</Typography>
        );
      }
    },
    { 
      field: "address",
      headerName: "Adresse",
      flex: 1.5,
      minWidth: 250,
      renderCell: (params) => {
        const club = params.row as Club;
        const address = getFullAddress(club);
        return (
          <Tooltip title={address} placement="top-start">
            <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {address}
            </Typography>
          </Tooltip>
        );
      }
    },
    { 
      field: "contactPerson", 
      headerName: "Ansprechpartner", 
      flex: 1, 
      minWidth: 200,
      renderCell: (params) => {
        const club = params.row as Club;
        const contactInfo = getContactInfo(club);
        return (
          <Tooltip title={contactInfo} placement="top-start">
            <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {contactInfo}
            </Typography>
          </Tooltip>
        );
      }
    },
    {
      field: "actions",
      headerName: "Aktionen",
      flex: 1,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => {
        const club = params.row as Club;
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="contained" 
              color="primary" 
              size="small" 
              startIcon={<EditIcon />} 
              onClick={() => handleEdit(club)}
            >
              Bearbeiten
            </Button>
            {userRole === "admin" && (
              <Button 
                variant="contained" 
                color="error" 
                size="small" 
                startIcon={<DeleteIcon />} 
                onClick={() => handleDelete(club)}
                disabled={true} // Delete button disabled as requested
                title="Löschen ist derzeit nicht verfügbar"
              >
                Löschen
              </Button>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Container component="main" maxWidth="xl">
      <Paper elevation={3} sx={{ padding: 3, mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">
            Vereinsverwaltung
          </Typography>
        </Box>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Box sx={{ height: 'auto', width: "100%", mt: 2 }}>
          <DataGrid
            rows={clubs}
            columns={columns}
            getRowId={(row) => row._id}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 },
              },
            }}
            autoHeight
            loading={loading}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 300 },
              },
            }}
            sx={{
              '& .MuiDataGrid-cell': { py: 1 },
              '& .MuiDataGrid-columnHeaders': { backgroundColor: 'background.default' },
            }}
          />
        </Box>
      </Paper>
      
      {/* Dialog for editing a club */}
      <ClubEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        club={selectedClub}
        onClubUpdated={handleClubUpdated}
      />
    </Container>
  );
};

export default ClubsManagementPage;